"use client";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { UsePostResult } from "@/src/CustomHooks/usePostResult";
import { API_ENDPOINTS } from "@/src/utilities/api/apiConfig";
import { useEffect } from "react";

// Zod schema for form validation
const inquirySchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  phone: z.string().optional(),
});

type InquirySchema = z.infer<typeof inquirySchema>;

interface InquiryFormProps {
  productId: string;
  onSuccess?: () => void;
}

export default function InquiryForm({ productId, onSuccess }: InquiryFormProps) {
  const { postData, loading, error, data } = UsePostResult();

  const form = useForm<InquirySchema>({
    resolver: zodResolver(inquirySchema),
    defaultValues: {
      name: "",
      email: "",
      companyName: "",
      phone: "",
    },
  });

  const onSubmit = (values: InquirySchema) => {
    // Prepare the data payload with product reference
    const payload = {
      data: {
        ...values,
        product: productId, // Associate the inquiry with the product
      },
    };

    postData(
      API_ENDPOINTS.INQUIRY,
      payload,
      {},
      "Inquiry submitted successfully!"
    );
  };

  useEffect(() => {
    if (data) {
      // Reset form after successful submission
      form.reset();
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [data, form, onSuccess]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Name Field */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <Label>Name *</Label>
              <FormControl>
                <Input
                  type="text"
                  placeholder="Enter your name"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Email Field */}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <Label>Email *</Label>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Company Name Field */}
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <Label>Company Name *</Label>
              <FormControl>
                <Input
                  type="text"
                  placeholder="Enter your company name"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Phone Field (Optional) */}
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <Label>Phone (Optional)</Label>
              <FormControl>
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Submit Button */}
        <div className="flex gap-2 justify-end pt-4">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Submitting..." : "Submit Inquiry"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
